import React, { useState, useEffect, useReducer } from "react";
import { useNavigate } from "react-router-dom";

// Styles
import "./styles.scss";

// Redux
import { useDispatch } from "react-redux";
import {
  createAssessmentBLrequest_ADMIN,
  getAccessorListForAssessmentBLrequest_ADMIN,
  getCountryListRequest_ADMIN,
  getIndustryListForAssessmentBLrequest_ADMIN,
  customerTypeRequest_ADMIN,
  getCompanyNameList_ADMIN,
  getUserListFromLastAssessment,
} from "../../../../Redux/Slices/Admin/adminSlice";
import { getCompanyType } from "../../../../Redux/Slices/Accessor/accessorSlice";

// Custom layout
import MainLayout from "Layouts/Main";

// Assets
import Colors from "../../../../Assets/SassConstants/colors.scss";
import Back from "../../../../Assets/Svg/Admin/AssessmentBacklog/backChevron.svg";
import Add from "../../../../Assets/Svg/Admin/AssessmentBacklog/add.svg";

// Utils
import { validateInput } from "Services/Utils/validateInput";

// Custom component
import InputBox from "Components/InputBox";
import ErrorMessage from "Components/ErrorMessage";
import Dropdown from "Components/Dropdown";
import DatePicker from "Components/DatePicker";
import CommonDatePicker from "Components/CommonDatePicker";
import CustomSelect from "Components/CustomSelect";

const inputStyle = {
  borderWidth: 1,
  borderColor: Colors.grey_18,
  fontSize: 13,
  color: Colors.black_1,
  boxShadow: "none",
};

const companyReducer = (state, action) => {
  switch (action.type) {
    case "SET_EXISTING_COMPANY":
      return {
        ...state,
        isExistingCompany: true,
        isNewCompanyName: false,
        companyData: action.payload || null,
      }
    case "SET_NEW_COMPANY":
      return {
        ...state,
        isExistingCompany: false,
        isNewCompanyName: true,
        companyData: null,
      };

    case "RESET_COMPANY":
      return {
        ...state,
        isExistingCompany: false,
        isNewCompanyName: false,
        companyData: null,
      };

    default:
      return state;
  }
}

const AddAssessmentBacklog = () => {
  // Initialize useNavigate
  const navigate = useNavigate();

  //Initialize redux
  const dispatch = useDispatch();

  const [currentComponent, setCurrentComponent] = useState("create-assessment");

  useEffect(() => {
    getDropdownValues();
  }, []);

  //==================== Company details start ====================
  const initialFormData = {
    newCompanyName: "",
    country: "",
    industry: "",
    assessmentName: "",
    keyContactName: "",
    email: "",
    designation: "",
    department: "",
    customerType: "",
    dueDate: "",
    accessor: "",
  };

  const initialFormErrors = {};

  const [formData, setFormData] = useState(initialFormData);
  const [formErrors, setFormErrors] = useState(initialFormErrors);
  const [countryList, setCountryList] = useState([]);
  const [companyNameList, setCompanyNameList] = useState([]);
  const [accessorList, setAccessorList] = useState([]);
  const [companyListDetails, setCompanyListDetails] = useState([]);
  const [industryList, setIndustryList] = useState([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [customerType, setCustomerType] = useState([]);
  const [typedValue, setTypedValue] = useState("");
  const [companyState, dispatchCompany] = useReducer(companyReducer, {
    isExistingCompany: false,
    isNewCompanyName: false,
    companyData: null,
  });

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prevData) => ({
      ...prevData,
      [name]: value,
    }));

    if (formErrors[name]) {
      setFormErrors((prevErrors) => ({
        ...prevErrors,
        [name]: "",
      }));
    }
  };

  // Validate form data based on type
  const handleValidation = () => {
    let validationErrors = {};
    Object.keys(formData).forEach((key) => {
      const error = validateInput(key, formData[key]);
      if (Object.keys(error).length > 0) {
        validationErrors = { ...validationErrors, ...error };
      }
    });
    setFormErrors(validationErrors);
    return validationErrors;
  };

  //To get all dropdown values
  const getDropdownValues = async () => {
    await getCountryList();
    await getIndustryList();
    await getAccessorList();
    // await getCompanyList();
    await getCustomerType();
    await getCompanyName();
  };

  //To get customer type
  const getCustomerType = () => {
    dispatch(
      customerTypeRequest_ADMIN({
        callBack: getCustomerTypeList,
      })
    );
  };
  const getCustomerTypeList = (customerType) => {
    const customers = customerType.map((customer) => ({
      label: customer.customer_type_name,
      value: customer.customer_type_id,
    }));
    setCustomerType(customers);
  };

  // Form submit handler
  const handleSubmit = (e) => {
    e.preventDefault();
    const validationErrors = handleValidation();
    if (Object.keys(validationErrors).length === 0) {
      // Handle form submission
      setCurrentComponent("add-users");
    }
  };
  //==================== Company details end ====================

  //==================== Add users start ====================
  const initialUserDetailsFormData = {
    user_name: "",
    email: "",
  };
  const initialUserDetailsFormErrors = {};

  const [userDetailsFormData, setUserDetailsFormData] = useState(
    initialUserDetailsFormData
  );
  const [userDetailsFormErrors, setUserDetailsFormErrors] = useState(
    initialUserDetailsFormErrors
  );
  const [users, setUsers] = useState([]);

  const handleUserDetailsChange = (e) => {
    const { name, value } = e.target;
    setUserDetailsFormData((prevData) => ({
      ...prevData,
      [name]: value,
    }));

    if (userDetailsFormErrors[name]) {
      setUserDetailsFormErrors((prevErrors) => ({
        ...prevErrors,
        [name]: "",
      }));
    }
  };

  // Validate form data based on type
  const handleUserDetailsValidation = () => {
    let validationErrors = {};
    Object.keys(userDetailsFormData).forEach((key) => {
      const error = validateInput(key, userDetailsFormData[key]);
      if (Object.keys(error).length > 0) {
        validationErrors = { ...validationErrors, ...error };
      }
    });

    setUserDetailsFormErrors(validationErrors);
    return validationErrors;
  };

  const handleAddUser = () => {
    const validationErrors = handleUserDetailsValidation();
    if (Object.keys(validationErrors).length === 0) {
      // Check for duplicate email in the users array
      const isDuplicate = users.some(
        (user) =>
          user.email.toLowerCase() === userDetailsFormData.email.toLowerCase()
      );

      if (isDuplicate) {
        setUserDetailsFormErrors((prevErrors) => ({
          ...prevErrors,
          email: "User with this email address is already added.",
        }));
        return;
      }

      // Add the user if not a duplicate
      setUsers([...users, userDetailsFormData]);
      setUserDetailsFormData(initialUserDetailsFormData);
    }
  };

  const handleDeleteUser = (index) => {
    const updatedUsers = users.filter((_, i) => i !== index);
    setUsers(updatedUsers);
  };

  //Post the form
  const send = () => {
    setIsSubmitting(true);
    dispatch(
      createAssessmentBLrequest_ADMIN({
        data: {
          company_name: formData?.newCompanyName?.label,
          industry_name: formData?.industry?.label,
          industry_type_id: formData?.industry?.value,
          keycontact_name: formData?.keyContactName,
          country_name: formData?.country?.label,
          country_id: formData?.country?.value,
          assessment_name: formData?.assessmentName,
          email: formData?.email,
          designation: formData?.designation,
          department: formData?.department,
          assessor_name: formData?.accessor?.label,
          customer_type_name: formData?.customerType?.label,
          customer_type_id: formData?.customerType?.value,
          assessor_id: formData?.accessor?.value,
          dueDate: formData?.dueDate,
          users: users,
        },
        callBack: createAssessmentBLrequestCB_ADMIN,
      })
    );
  };

  //Handle the response of createAssessmentBLrequest_ADMIN
  const createAssessmentBLrequestCB_ADMIN = () => {
    setFormData(initialFormData);
    setFormErrors(initialFormErrors);
    setUserDetailsFormData(initialUserDetailsFormData);
    setUserDetailsFormErrors(initialUserDetailsFormErrors);
    setIsSubmitting(false);
    setUsers([]);
    navigate("/assessment-backlogs-success", {
      replace: true,
    });
  };

  //Get country list
  const getCountryList = () => {
    dispatch(
      getCountryListRequest_ADMIN({
        callBack: getCountryListRequestCB_ADMIN,
      })
    );
  };

  // Handle the response get country list
  const getCountryListRequestCB_ADMIN = (countries) => {
    const countryOptions = countries.map((country) => ({
      label: country.country_name,
      value: country.country_id,
    }));
    setCountryList(countryOptions);
  };

  //Get accessor list
  const getAccessorList = () => {
    dispatch(
      getAccessorListForAssessmentBLrequest_ADMIN({
        callBack: getAccessorListForAssessmentBLrequestCB_ADMIN,
      })
    );
  };

  // Handle the response of getAccessorListForAssessmentBLrequest_ADMIN
  const getAccessorListForAssessmentBLrequestCB_ADMIN = (accessors) => {
    const accessorOptions = accessors.map((accessor) => ({
      label: accessor.user_name,
      value: accessor.user_id,
    }));
    setAccessorList(accessorOptions);
  };

  //Get indyustry list
  const getIndustryList = () => {
    dispatch(
      getIndustryListForAssessmentBLrequest_ADMIN({
        callBack: getIndustryListForAssessmentBLrequestCB_ADMIN,
      })
    );
  };

  const getIndustryListForAssessmentBLrequestCB_ADMIN = (industries) => {
    const industryOptions = industries.map((industry) => ({
      label: industry.industry_name,
      value: industry.industry_type_id,
    }));
    setIndustryList(industryOptions);
  };

  // const handleCompanyChange = (option) => {
  //   setFormData((prevData) => ({
  //     ...prevData,
  //     ["companyType"]: option,
  //   }));

  //   if (formErrors["companyType"]) {
  //     setFormErrors((prevErrors) => ({
  //       ...prevErrors,
  //       ["companyType"]: "",
  //     }));
  //   }
  // };

  const getCompanyName = () => {
    dispatch(
      getCompanyNameList_ADMIN({
        callBack: getCompanyNameListRequestCB_ADMIN
      })
    )
  }

  const getCompanyNameListRequestCB_ADMIN = (companies) => {
    const companyNameListOptions = companies?.map((name) => ({
      label: name?.company_name,
      value: name?.company_name
    }))

    setCompanyListDetails(companies)
    setCompanyNameList(companyNameListOptions)
  }

  const fetchUserListFromLastAssessment = (companyName) => {
    dispatch(
      getUserListFromLastAssessment({
        company_name: companyName,
        callBack: getUsersListWithCompanyAssessment
      })
    )
  }

  const getUsersListWithCompanyAssessment = (value) => {
    const arrValue = Array.isArray(value) ? value : [value]
    const cleanedUserObj = arrValue.map(item => ({
      user_name: item.user_name,
      email: item.email
    }));

    setUsers(cleanedUserObj);
  }

  const handleDateChange = (date) => {
    setFormData((prev) => ({ ...prev, dueDate: date }));
    setFormErrors((prev) => ({ ...prev, dueDate: "" }));
  };

  const handleCompanyNameChange = (option) => {
    const companyNameValue = option?.label
    const companyDetailsData = companyListDetails?.find((item) => item?.company_name.toLowerCase() === companyNameValue.toLowerCase())
    if (companyDetailsData) {
      fetchUserListFromLastAssessment(companyDetailsData?.company_name)
      const selectedCountry = countryList.find((c) => c?.label === companyDetailsData?.country_name);
      const selectedIndustry = industryList.find((i) => i?.label === companyDetailsData?.industry_name);
      const selectedCustomerType = customerType.find((c) => c?.label === companyDetailsData?.customer_type_name)

      if (selectedCountry && selectedIndustry && selectedCustomerType) {
        dispatchCompany({
          type: "SET_EXISTING_COMPANY",
          payload: companyDetailsData,
        });
      }

      setFormData((prevData) => ({
        ...prevData,
        ["newCompanyName"]: option,
        ["country"]: selectedCountry,
        ["industry"]: selectedIndustry,
        ["customerType"]: selectedCustomerType,
        ["keyContactName"]: companyDetailsData?.keycontact_name,
        ["email"]: companyDetailsData?.email,
        ["designation"]: companyDetailsData?.designation,
        ["department"]: companyDetailsData?.department,
      }));

      setFormErrors((prevErrors) => ({
        ...prevErrors,
        newCompanyName: "",
        country: "",
        industry: "",
        customerType: "",
        keyContactName: "",
        email: "",
        designation: "",
        department: ""
      }));
    } else {
      dispatchCompany({
        type: "SET_NEW_COMPANY",
      });
      handleAddCompanyName(option)
    }
  }

  const handleInputChange = (value) => {
    setTypedValue(value);
    const companyDetailsData = companyListDetails?.find((item) => item?.company_name.toLowerCase() === value.toLowerCase());
    if (companyDetailsData) {
      dispatchCompany({ type: "SET_EXISTING_COMPANY", payload: companyDetailsData });
    } else {
      dispatchCompany({ type: "SET_NEW_COMPANY" });
    }
  };


  const handleAddCompanyName = (option) => {
    const newOption = {
      label: option.label,
      value: option.label,
    };
    setFormData((prevData) => ({
      ...prevData,
      ["newCompanyName"]: newOption,
      ["country"]: "",
      ["industry"]: "",
      ["keyContactName"]: "",
      ["email"]: "",
      ["designation"]: "",
      ["department"]: "",
      ["customerType"]: "",
    }));

    if (formErrors["newCompanyName", "country"]) {
      setFormErrors((prevErrors) => ({
        ...prevErrors,
        ["newCompanyName"]: "",
        ["country"]: ""
      }))
    }
    fetchUserListFromLastAssessment(option.label);
  }

  const handleCountryChange = (option) => {
    setFormData((prevData) => ({
      ...prevData,
      ["country"]: option,
    }));

    if (formErrors["country"]) {
      setFormErrors((prevErrors) => ({
        ...prevErrors,
        ["country"]: "",
      }));
    }
  };

  const handleIndustryChange = (option) => {
    setFormData((prevData) => ({
      ...prevData,
      ["industry"]: option,
    }));

    if (formErrors["industry"]) {
      setFormErrors((prevErrors) => ({
        ...prevErrors,
        ["industry"]: "",
      }));
    }
  };

  const handleAssessorChange = (option) => {
    setFormData((prevData) => ({
      ...prevData,
      ["accessor"]: option,
    }));

    if (formErrors["accessor"]) {
      setFormErrors((prevErrors) => ({
        ...prevErrors,
        ["accessor"]: "",
      }));
    }
  };

  const handleCustomerTypeChange = (option) => {
    setFormData((prevData) => ({
      ...prevData,
      customerType: option,
    }));

    if (formErrors.customerType) {
      setFormErrors((prevErrors) => ({
        ...prevErrors,
        customerType: "",
      }));
    }
  };
  //for style
  const currentStep = 2;
  //==================== Add users end ====================

  return (
    <MainLayout style={{ backgroundColor: Colors.grey_12 }}>
      {currentComponent === "create-assessment" ? (
        <div className="backlog-form-assessment-page">
          <div
            onClick={() => navigate("/assessment-backlogs")}
            className="backlog-form-back-option"
          >
            <img src={Back} alt="Back" />
            <span className="backlog-form-back-text">
              Back to Assessment Backlog
            </span>
          </div>

          <div className="backlog-form-status-container">
            <div className="backlog-form-completion-status">
              {/* Step 1 */}
              <div className="backlog-form-step">
                <div
                  className={`backlog-form-circle ${currentStep >= 1 ? "active" : ""
                    }`}
                >
                  <div></div>
                </div>
                <div
                  className={`backlog-form-step-text ${currentStep >= 1 ? "active" : ""
                    }`}
                >
                  Company Details
                </div>
                <div className="backlog-form-step-label">STEP 1</div>
              </div>

              {/* Line between steps */}
              <div
                className={`backlog-form-line ${currentStep >= 2 ? "active" : ""
                  }`}
              ></div>

              {/* Step 2 */}
              <div className="backlog-form-step">
                <div
                  className={`backlog-form-circle ${currentStep === 2 ? "" : "active"
                    }`}
                >
                  <div></div>
                </div>
                <div
                  className={`backlog-form-step-text ${currentStep === 2 ? "" : "active"
                    }`}
                >
                  Add Users
                </div>
                <div className="backlog-form-step-label">STEP 2</div>
              </div>
            </div>
          </div>

          <form onSubmit={handleSubmit}>
            <div className="add-user-assessment-container">
              <div className="add-user-assessment-title">Create Assessment</div>
              <hr className="add-user-divider" />
              <div className="add-user-input-container">
                <div className="add-user-input-group">
                  {/* <div>
                    <InputBox
                      label="Company Name"
                      name="companyName"
                      labelStyle={{ fontWeight: "600", color: "#2D3436" }}
                      value={formData.companyName}
                      onChange={handleChange}
                      className="add-user-input-box"
                      required
                      inputStyle={inputStyle}
                    />
                    <ErrorMessage message={formErrors.companyName} />
                  </div> */}
                  <div style={{ display: "flex" }}>
                    <CustomSelect
                      label="Company Name"
                      name="newCompanyName"
                      required
                      labelStyle={{ fontWeight: "600", color: "#2D3436" }}
                      options={companyNameList}
                      value={formData?.newCompanyName}
                      onChange={handleCompanyNameChange}
                      placeholder="Company Name"
                      isMulti={false}
                      defaultBorderColor="#c1c7cd"
                      height="60px"
                      isNewCompanyName={companyState.isNewCompanyName}
                      handleAddCompanyName={handleAddCompanyName}
                      updateIsCompanyName={handleInputChange}
                      typedValue={typedValue}
                    />
                    <ErrorMessage message={formErrors.newCompanyName} />
                  </div>
                  <div>
                    <CustomSelect
                      label="Country"
                      name="country"
                      required
                      labelStyle={{ fontWeight: "600", color: "#2D3436" }}
                      options={countryList}
                      value={formData?.country}
                      onChange={handleCountryChange}
                      placeholder="Country"
                      isMulti={false}
                      defaultBorderColor="#c1c7cd"
                      height="60px"
                    />
                    <ErrorMessage message={formErrors.country} />
                  </div>
                </div>

                <div className="add-user-input-group">
                  <div>
                    <CustomSelect
                      label="Industry"
                      name="industry"
                      required
                      labelStyle={{ fontWeight: "600", color: "#2D3436" }}
                      options={industryList}
                      value={formData?.industry}
                      onChange={handleIndustryChange}
                      placeholder="Industry"
                      isMulti={false}
                      defaultBorderColor="#c1c7cd"
                      height="60px"
                    />
                    <ErrorMessage message={formErrors.industry} />
                  </div>
                  <div>
                    <InputBox
                      label="Assessment Name"
                      name="assessmentName"
                      labelStyle={{ fontWeight: "600", color: "#2D3436" }}
                      value={formData.assessmentName}
                      onChange={handleChange}
                      className="add-user-input-box"
                      required
                      inputStyle={inputStyle}
                    />
                    <ErrorMessage message={formErrors.assessmentName} />
                  </div>
                </div>

                <div className="add-user-input-group">
                  <div>
                    <InputBox
                      label="Key Contact Name"
                      name="keyContactName"
                      labelStyle={{ fontWeight: "600", color: "#2D3436" }}
                      value={formData.keyContactName}
                      onChange={handleChange}
                      className="add-user-input-box"
                      required
                      inputStyle={inputStyle}
                    />
                    <ErrorMessage message={formErrors.keyContactName} />
                  </div>
                  <div>
                    <InputBox
                      label="Email Address"
                      name="email"
                      labelStyle={{ fontWeight: "600", color: "#2D3436" }}
                      value={formData.email}
                      onChange={handleChange}
                      className="add-user-input-box"
                      required
                      inputStyle={inputStyle}
                    />
                    <ErrorMessage message={formErrors.email} />
                  </div>
                </div>

                <div className="add-user-input-group">
                  <div>
                    <InputBox
                      label="Designation"
                      name="designation"
                      labelStyle={{ fontWeight: "600", color: "#2D3436" }}
                      value={formData.designation}
                      onChange={handleChange}
                      className="add-user-input-box"
                      required
                      inputStyle={inputStyle}
                    />
                    <ErrorMessage message={formErrors.designation} />
                  </div>
                  <div>
                    <InputBox
                      label="Department"
                      name="department"
                      labelStyle={{ fontWeight: "600", color: "#2D3436" }}
                      value={formData.department}
                      onChange={handleChange}
                      className="add-user-input-box"
                      required
                      inputStyle={inputStyle}
                    />
                    <ErrorMessage message={formErrors.department} />
                  </div>
                </div>

                <div className="add-user-input-group">
                  <div>
                    <div className="dateLabel">Due Date *</div>
                    <CommonDatePicker
                      name="dueDate"
                      value={formData.dueDate}
                      onChange={handleDateChange}
                      placeholder={"DD/MM/YYYY"}
                      required
                      customStyle={{
                        borderRadius: "15px",
                        border: "2px solid #c1c7cd",
                        height: "59px",
                      }}
                      minDate={
                        new Date(new Date().setDate(new Date().getDate() + 1))
                          .toISOString()
                          .split("T")[0]
                      }
                    />
                    <ErrorMessage message={formErrors.dueDate} />
                  </div>
                  <div>
                    <CustomSelect
                      label="Customer Type"
                      name="customerType"
                      required
                      labelStyle={{ fontWeight: "600", color: "#2D3436" }}
                      options={customerType}
                      value={formData?.customerType}
                      onChange={handleCustomerTypeChange}
                      placeholder="Customer Type"
                      isMulti={false}
                      defaultBorderColor="#c1c7cd"
                      height="58px"
                    />
                    <ErrorMessage message={formErrors.customerType} />
                  </div>
                </div>
              </div>
            </div>

            <div className="add-user-assessment-container">
              <div className="add-user-assessment-title">Assign Assessor</div>
              <hr className="add-user-divider" />
              <div className="add-user-input-container">
                <div className="add-user-input-group">
                  <div>
                    <CustomSelect
                      label="Select Assessor"
                      name="assessor"
                      required
                      labelStyle={{ fontWeight: "600", color: "#2D3436" }}
                      options={accessorList}
                      value={formData?.accessor}
                      onChange={handleAssessorChange}
                      placeholder="Assessor"
                      isMulti={false}
                      defaultBorderColor="#c1c7cd"
                      height="58px"
                    />
                    <ErrorMessage message={formErrors.accessor} />
                  </div>
                  <div></div>
                </div>
              </div>
            </div>

            <div className="assessment-backlog-form-buttons-container">
              <button
                onClick={() => navigate("/assessment-backlogs")}
                type="button"
                className="assessment-backlog-form-cancel-button"
              >
                Cancel
              </button>
              <button
                type="submit"
                className="assessment-backlog-form-save-button"
                onClick={handleSubmit}
              >
                Save & Continue
              </button>
            </div>
          </form>
        </div>
      ) : (
        <div className="backlog-form-assessment-page">
          <div
            onClick={() => {
              setCurrentComponent("create-assessment");
            }}
            className="backlog-form-back-option"
          >
            <img src={Back} alt="Back" />
            <span className="backlog-form-back-text">
              Back to Company Details
            </span>
          </div>

          <div className="backlog-form-status-container">
            <div className="backlog-form-completion-status">
              <div className="backlog-form-step">
                <div className="backlog-form-circle active"></div>
                <div className="backlog-form-step-text active">
                  Company details
                </div>
                <div className="backlog-form-step-label">STEP 1</div>
              </div>

              <div className="backlog-form-line"></div>

              <div className="backlog-form-step">
                <div className="backlog-form-circle active"></div>
                <div className="backlog-form-step-text active">Add Users</div>
                <div className="backlog-form-step-label">STEP 2</div>
              </div>
            </div>
          </div>

          <div className="add-user-assessment-container">
            <div className="add-user-assessment-title">Add Users</div>
            <hr className="add-user-divider" />
            <div className="add-user-input-container">
              <div className="add-user-input-group">
                <div>
                  <InputBox
                    label="Name"
                    name="user_name"
                    value={userDetailsFormData.user_name}
                    onChange={handleUserDetailsChange}
                    className="add-user-input-box"
                    required
                    inputStyle={inputStyle}
                  />
                  <ErrorMessage message={userDetailsFormErrors.user_name} />
                </div>
                <div>
                  <InputBox
                    label="Email Address"
                    name="email"
                    value={userDetailsFormData.email}
                    onChange={handleUserDetailsChange}
                    className="add-user-input-box"
                    required
                    inputStyle={inputStyle}
                  />
                  <ErrorMessage message={userDetailsFormErrors.email} />
                </div>
              </div>
            </div>
            <div className="add-user-add-button">
              <img onClick={handleAddUser} src={Add} alt="Add" />
            </div>
            <hr className="add-users-divider" />
            <div className="addded-user-assessment-title">Added Users</div>

            {users?.length > 0 ? (
              <div className="add-users-form-user-container">
                <div className="add-users-form-user-header">
                  <div>S.No</div>
                  <div style={{ maxWidth: "192px" }}>Name</div>
                  <div>Email Address</div>
                  <div></div>
                </div>
                <div className="add-users-form-user-body">
                  {users.map((user, index) => (
                    <div key={index} className="add-users-form-user-row">
                      <div>{index + 1}</div>
                      <div
                        style={{
                          maxWidth: "192px",
                          wordBreak: "break-word",
                          whiteSpace: "normal",
                        }}
                      >
                        {user.user_name}
                      </div>
                      <div
                        style={{
                          marginLeft: "0px",
                          wordBreak: "break-word",
                          whiteSpace: "normal",
                        }}
                      >
                        {user.email}
                      </div>
                      <div className="add-users-form-delete-column">
                        <div
                          onClick={() => handleDeleteUser(index)}
                          className="added-users-form-delete"
                        >
                          Delete
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
                {/* Buttons */}
                <div className="add-users-form-button-container">
                  <button
                    onClick={() => setUsers([])}
                    type="button"
                    className="added-users-form-clear-button"
                  >
                    Clear
                  </button>
                  <button
                    type="button"
                    onClick={send}
                    disabled={isSubmitting}
                    className="added-users-send-button"
                  >
                    Send
                  </button>
                </div>
              </div>
            ) : (
              <div className="added-users-no-users">
                <p>No Users!</p>
              </div>
            )}
          </div>

          <div className="added-users-backtohome-container">
            <button
              type="button"
              onClick={() => navigate(-1)}
              className="added-users-backtohome-btn"
            >
              Back To Home
            </button>
          </div>
        </div>
      )}
    </MainLayout>
  );
};

export default AddAssessmentBacklog;
